<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <router-link to="/super-admin/loyalty/tiers" class="gl-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Tiers
        </router-link>
        <h1 class="gl-page-title">{{ isEdit ? 'Edit Tier' : 'Add Tier' }}</h1>
        <p class="gl-page-sub">{{ isEdit ? 'Update tier configuration and benefits' : 'Define a new membership tier' }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="gl-tf-layout">
      <div class="gl-tf-card" style="display:flex;flex-direction:column;gap:16px;padding:24px">
        <div v-for="i in 6" :key="i">
          <div class="gl-skel" style="width:80px;height:10px;margin-bottom:8px;border-radius:6px"></div>
          <div class="gl-skel" style="width:100%;height:40px;border-radius:9px"></div>
        </div>
      </div>
    </div>

    <div v-else class="gl-tf-layout">
      <!-- Form -->
      <div class="gl-tf-main">
        <form @submit.prevent="submitForm" style="display:flex;flex-direction:column;gap:28px">

          <!-- Identity -->
          <div class="gl-tf-card">
            <div class="gl-tf-section-title">Tier Identity</div>
            <div class="gl-tf-row-2">
              <div class="gl-tf-field">
                <label class="gl-tf-label">Name <span class="gl-required">*</span></label>
                <input type="text" class="gl-input" :class="{ 'gl-input-error': errors.name }" v-model="form.name" placeholder="e.g. Gold" required>
                <div v-if="errors.name" class="gl-field-error">{{ errors.name[0] }}</div>
              </div>
              <div class="gl-tf-field">
                <label class="gl-tf-label">Slug <span class="gl-tf-label-opt">(optional)</span></label>
                <input type="text" class="gl-input gl-input-mono" :class="{ 'gl-input-error': errors.slug }" v-model="form.slug" placeholder="auto-generated from name">
                <div class="gl-field-hint">Lowercase identifier, e.g. gold</div>
                <div v-if="errors.slug" class="gl-field-error">{{ errors.slug[0] }}</div>
              </div>
            </div>
          </div>

          <!-- Requirement -->
          <div class="gl-tf-card">
            <div class="gl-tf-section-title">Tier Requirement</div>
            <div class="gl-tf-field">
              <label class="gl-tf-label">Min Orders Threshold <span class="gl-required">*</span></label>
              <input type="number" min="0" class="gl-input" :class="{ 'gl-input-error': errors.min_orders }" v-model.number="form.min_orders" required>
              <div class="gl-field-hint">Completed orders within the rolling window to reach this tier. Set 0 for the starting tier.</div>
              <div v-if="errors.min_orders" class="gl-field-error">{{ errors.min_orders[0] }}</div>
            </div>
          </div>

          <!-- Member Perks (enforced at checkout) -->
          <div class="gl-tf-card">
            <div class="gl-tf-section-title">Member Perks</div>
            <div class="gl-tf-row-2">
              <div class="gl-tf-field">
                <label class="gl-tf-label">Points Multiplier <span class="gl-required">*</span></label>
                <div class="gl-input-addon-wrap">
                  <input type="number" step="0.01" min="0" class="gl-input" :class="{ 'gl-input-error': errors.points_multiplier }" v-model.number="form.points_multiplier" required>
                  <span class="gl-input-addon">×</span>
                </div>
                <div class="gl-field-hint">e.g. 1.50 means 1.5× base earning rate</div>
                <div v-if="errors.points_multiplier" class="gl-field-error">{{ errors.points_multiplier[0] }}</div>
              </div>
              <div class="gl-tf-field">
                <label class="gl-tf-label">Member Discount</label>
                <div class="gl-input-addon-wrap">
                  <input type="number" step="0.5" min="0" max="100" class="gl-input" :class="{ 'gl-input-error': errors.discount_percent }" v-model.number="form.discount_percent">
                  <span class="gl-input-addon">%</span>
                </div>
                <div class="gl-field-hint">Off every order's subtotal. 0 = none</div>
                <div v-if="errors.discount_percent" class="gl-field-error">{{ errors.discount_percent[0] }}</div>
              </div>
              <div class="gl-tf-field">
                <label class="gl-tf-label">Birthday Discount</label>
                <div class="gl-input-addon-wrap">
                  <input type="number" step="0.5" min="0" max="100" class="gl-input" :class="{ 'gl-input-error': errors.birthday_discount_percent }" v-model.number="form.birthday_discount_percent">
                  <span class="gl-input-addon">%</span>
                </div>
                <div class="gl-field-hint">Extra % during the member's birthday month. 0 = none</div>
                <div v-if="errors.birthday_discount_percent" class="gl-field-error">{{ errors.birthday_discount_percent[0] }}</div>
              </div>
              <div class="gl-tf-field">
                <label class="gl-tf-label">Free Delivery</label>
                <div class="gl-tf-toggle-row" style="margin-top:8px">
                  <div class="gl-field-hint" style="margin:0">Waive the rider delivery fee</div>
                  <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': form.free_delivery }" @click="form.free_delivery = !form.free_delivery">
                    <span class="gl-toggle-thumb"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Extra highlights (display only) -->
          <div class="gl-tf-card">
            <div class="gl-tf-section-title">Extra Highlights <span class="gl-tf-label-opt">(display only)</span></div>
            <div class="gl-field-hint" style="margin:-8px 0 14px">Free-text bullets shown to customers. Not enforced — use the Member Perks above for anything that affects pricing or points.</div>
            <div class="gl-benefits-list">
              <div v-for="(b, i) in form.benefits" :key="i" class="gl-benefit-row">
                <input
                  type="text"
                  class="gl-input"
                  v-model="form.benefits[i]"
                  placeholder="e.g. Priority support, early access to drops"
                >
                <button type="button" class="gl-benefit-remove" @click="removeBenefit(i)" title="Remove">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div v-if="!form.benefits.length" class="gl-benefits-empty">No extra highlights added</div>
            </div>
            <button type="button" class="gl-add-benefit-btn" @click="addBenefit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Highlight
            </button>
          </div>

          <!-- Options -->
          <div class="gl-tf-card">
            <div class="gl-tf-section-title">Options</div>
            <div class="gl-tf-row-2">
              <div class="gl-tf-field">
                <label class="gl-tf-label">Sort Order</label>
                <input type="number" min="0" class="gl-input" v-model.number="form.sort_order">
                <div class="gl-field-hint">Lower numbers appear first</div>
              </div>
              <div class="gl-tf-field">
                <label class="gl-tf-label">Default Tier</label>
                <div class="gl-tf-toggle-row" style="margin-top:8px">
                  <div class="gl-field-hint" style="margin:0">Assign to new customers automatically</div>
                  <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': form.is_default }" @click="form.is_default = !form.is_default">
                    <span class="gl-toggle-thumb"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="gl-tf-actions">
            <router-link to="/super-admin/loyalty/tiers" class="gl-btn-ghost">Cancel</router-link>
            <button type="submit" class="gl-btn-primary" :disabled="submitting">
              <span v-if="submitting" class="gl-spin"></span>
              {{ isEdit ? 'Update Tier' : 'Create Tier' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Sidebar Preview -->
      <div class="gl-tf-sidebar">
        <div class="gl-tf-preview-card">
          <div class="gl-tf-preview-title">Live Preview</div>
          <div class="gl-tf-preview-head" :style="{ background: previewGradient }">
            <div class="gl-tf-preview-emoji">{{ previewEmoji }}</div>
            <div class="gl-tf-preview-name">{{ form.name || 'Tier Name' }}</div>
            <div class="gl-tf-preview-slug">{{ form.slug || 'slug' }}</div>
          </div>
          <div class="gl-tf-preview-stats">
            <div class="gl-tf-preview-row">
              <span>Min orders</span>
              <strong>{{ (form.min_orders || 0).toLocaleString() }}</strong>
            </div>
            <div class="gl-tf-preview-row">
              <span>Multiplier</span>
              <strong>{{ Number(form.points_multiplier || 0).toFixed(2) }}×</strong>
            </div>
            <div class="gl-tf-preview-row">
              <span>Default</span>
              <strong>{{ form.is_default ? 'Yes' : 'No' }}</strong>
            </div>
          </div>
          <div class="gl-tf-preview-benefits">
            <div v-for="(b, i) in previewPerks" :key="i" class="gl-tf-preview-benefit">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#900C27" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {{ b }}
            </div>
            <div v-if="!previewPerks.length" class="gl-tf-preview-empty" style="padding:0">No perks configured yet</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminTierApi } from '@/services/superAdminApi';

const TIER_THEMES = [
  'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
  'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
];
const TIER_EMOJIS = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '💠', free: '⭐', starter: '🌱', premium: '👑', vip: '🏆' };

export default {
  name: 'SATierForm',
  data() {
    return {
      loading: false, submitting: false, isEdit: false, errors: {},
      form: { name: '', slug: '', min_orders: 0, points_multiplier: 1.0, free_delivery: false, discount_percent: 0, birthday_discount_percent: 0, benefits: [], sort_order: null, is_default: false }
    };
  },
  computed: {
    nonEmptyBenefits() { return this.form.benefits.filter(b => b && b.trim()); },
    // Mirrors the backend perk_lines accessor so the preview matches the app.
    previewPerks() {
      const trim = (n) => Number(n || 0).toFixed(2).replace(/\.?0+$/, '');
      const lines = [];
      if (Number(this.form.points_multiplier) > 1) lines.push(`${trim(this.form.points_multiplier)}× points`);
      if (this.form.free_delivery) lines.push('Free delivery');
      if (Number(this.form.discount_percent) > 0) lines.push(`${trim(this.form.discount_percent)}% off every order`);
      if (Number(this.form.birthday_discount_percent) > 0) lines.push(`${trim(this.form.birthday_discount_percent)}% birthday discount`);
      return [...lines, ...this.nonEmptyBenefits];
    },
    previewGradient() {
      const slug = (this.form.slug || this.form.name || '').toLowerCase();
      for (const [key] of Object.entries(TIER_EMOJIS)) {
        if (slug.includes(key)) {
          const map = { bronze: TIER_THEMES[2], silver: TIER_THEMES[1], gold: TIER_THEMES[2], platinum: TIER_THEMES[3], diamond: TIER_THEMES[3], free: TIER_THEMES[0], starter: TIER_THEMES[0], premium: TIER_THEMES[3], vip: TIER_THEMES[3] };
          if (map[key]) return map[key];
        }
      }
      return TIER_THEMES[(this.form.sort_order ?? 0) % TIER_THEMES.length];
    },
    previewEmoji() {
      const slug = (this.form.slug || this.form.name || '').toLowerCase();
      for (const [key, emoji] of Object.entries(TIER_EMOJIS)) {
        if (slug.includes(key)) return emoji;
      }
      return '⭐';
    }
  },
  mounted() {
    if (this.$route.params.id) { this.isEdit = true; this.fetchTier(); }
  },
  methods: {
    async fetchTier() {
      try {
        this.loading = true;
        const r = await superAdminTierApi.get(this.$route.params.id);
        const t = r.data.data;
        this.form = {
          name: t.name, slug: t.slug || '',
          min_orders: t.min_orders ?? 0,
          points_multiplier: Number(t.points_multiplier),
          free_delivery: !!t.free_delivery,
          discount_percent: Number(t.discount_percent ?? 0),
          birthday_discount_percent: Number(t.birthday_discount_percent ?? 0),
          benefits: this.parseBenefits(t.benefits),
          sort_order: t.sort_order, is_default: !!t.is_default,
        };
      } catch (e) { console.error(e); this.$router.push('/super-admin/loyalty/tiers'); }
      finally { this.loading = false; }
    },
    parseBenefits(b) {
      if (!b) return [];
      if (Array.isArray(b)) return b;
      if (typeof b === 'string') { try { const p = JSON.parse(b); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    },
    addBenefit() { this.form.benefits.push(''); },
    removeBenefit(i) { this.form.benefits.splice(i, 1); },
    async submitForm() {
      this.errors = {};
      try {
        this.submitting = true;
        const data = { ...this.form, benefits: this.nonEmptyBenefits };
        if (!data.slug) delete data.slug;
        if (data.sort_order === null || data.sort_order === '') delete data.sort_order;
        if (this.isEdit) { await superAdminTierApi.update(this.$route.params.id, data); }
        else { await superAdminTierApi.create(data); }
        this.$router.push('/super-admin/loyalty/tiers');
      } catch (e) {
        if (e.response?.data?.errors) this.errors = e.response.data.errors;
        else alert(e.response?.data?.message || 'Failed to save tier');
      } finally { this.submitting = false; }
    }
  }
};
</script>

<style>
/* ── Layout (all prefixed gl-tf- to avoid global conflicts) ── */
.gl-tf-layout { display: grid; grid-template-columns: 1fr 300px; gap: 20px; align-items: start; }
.gl-tf-main { display: flex; flex-direction: column; gap: 28px; }
.gl-tf-sidebar { display: flex; flex-direction: column; gap: 16px; }

/* ── Form cards ── */
.gl-tf-card { background: #fff; border: 1px solid #e0e7e5; border-radius: 14px; padding: 22px 24px; box-shadow: 0 2px 8px rgba(0,0,0,.05); }
.gl-tf-section-title { font-size: 11.5px; font-weight: 700; color: #6b7f7b; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 18px; }
.gl-tf-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-tf-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.gl-tf-field:last-child { margin-bottom: 0; }
.gl-tf-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-tf-label-opt { font-weight: 400; color: #9ca3af; font-size: 11.5px; }
.gl-tf-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.gl-tf-actions { display: flex; justify-content: flex-end; gap: 10px; padding: 4px 0; }

/* ── Sidebar preview card ── */
.gl-tf-preview-card { background: #fff; border: 1px solid #e0e7e5; border-radius: 14px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,.05); }
.gl-tf-preview-head { padding: 24px 20px; text-align: center; color: #fff; }
.gl-tf-preview-emoji { font-size: 32px; margin-bottom: 8px; }
.gl-tf-preview-name { font-size: 18px; font-weight: 800; }
.gl-tf-preview-slug { font-size: 11.5px; opacity: .75; font-family: 'SF Mono', monospace; }
.gl-tf-preview-title { font-size: 11px; font-weight: 700; color: #6b7f7b; text-transform: uppercase; letter-spacing: .7px; padding: 14px 18px 0; }
.gl-tf-preview-stats { padding: 10px 18px 14px; border-bottom: 1px solid #f0f3f2; display: flex; flex-direction: column; gap: 8px; }
.gl-tf-preview-row { display: flex; justify-content: space-between; font-size: 13px; }
.gl-tf-preview-row span { color: #9ca3af; }
.gl-tf-preview-row strong { color: #111827; }
.gl-tf-preview-benefits { padding: 14px 18px; display: flex; flex-direction: column; gap: 6px; }
.gl-tf-preview-benefit { display: flex; align-items: flex-start; gap: 6px; font-size: 12.5px; color: #374151; }
.gl-tf-preview-empty { padding: 14px 18px; font-size: 12px; color: #9ca3af; font-style: italic; }

/* ── Shared primitives (inputs, toggle, buttons, etc.) ── */
.gl-required { color: #dc2626; }
.gl-field-hint { font-size: 11.5px; color: #9ca3af; }
.gl-field-error { font-size: 12px; color: #dc2626; }
.gl-input { width: 100%; padding: 10px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; transition: border-color .15s; }
.gl-input:focus { border-color: #900C27; }
.gl-input-error { border-color: #dc2626 !important; }
.gl-input-mono { font-family: 'SF Mono', 'Fira Code', monospace; }
.gl-input-addon-wrap { position: relative; }
.gl-input-addon-wrap .gl-input { padding-right: 36px; }
.gl-input-addon { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); font-size: 13px; color: #9ca3af; font-weight: 700; pointer-events: none; }
.gl-benefits-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.gl-benefit-row { display: flex; gap: 8px; align-items: center; }
.gl-benefit-row .gl-input { flex: 1; }
.gl-benefit-remove { width: 34px; height: 34px; flex-shrink: 0; border: 1.5px solid #e0e7e5; border-radius: 8px; background: #fff; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.gl-benefit-remove:hover { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.gl-benefits-empty { font-size: 12.5px; color: #9ca3af; font-style: italic; text-align: center; padding: 8px 0; }
.gl-add-benefit-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1.5px dashed #c8d5d2; border-radius: 9px; background: transparent; color: #6b7f7b; font-size: 13px; font-weight: 500; cursor: pointer; width: 100%; justify-content: center; transition: all .15s; }
.gl-add-benefit-btn:hover { border-color: #900C27; color: #900C27; background: #e8f4f1; }
.gl-toggle { width: 44px; height: 24px; border-radius: 12px; background: #e0e7e5; border: none; cursor: pointer; position: relative; transition: background .2s; flex-shrink: 0; padding: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; display: block; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 10px 20px; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none; }
.gl-btn-primary:hover { background: #1a5048; }
.gl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 10px 20px; font-size: 14px; font-weight: 500; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }
.gl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }

/* ── Responsive ── */
@media (max-width: 900px) { .gl-tf-layout { grid-template-columns: 1fr; } }
@media (max-width: 767px) {
  .gl-tf-row-2 { grid-template-columns: 1fr; gap: 0; }
  .gl-tf-card { padding: 16px; }
  .gl-tf-actions { flex-direction: column-reverse; }
  .gl-tf-actions .gl-btn-primary,
  .gl-tf-actions .gl-btn-ghost { width: 100%; justify-content: center; }
}

/* ── Dark mode ── */
body.dark-only .gl-tf-card,
body.dark-only .gl-tf-preview-card { background: #1e2a27; border-color: #2d3f3b; box-shadow: none; }
body.dark-only .gl-tf-label { color: #e2e8f0; }
body.dark-only .gl-tf-preview-row strong { color: #f1f5f9; }
body.dark-only .gl-tf-preview-stats { border-color: #2d3f3b; }
body.dark-only .gl-tf-preview-benefit { color: #e2e8f0; }
body.dark-only .gl-input { background: #162220; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-input:focus { border-color: #900C27; }
body.dark-only .gl-benefit-remove { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-add-benefit-btn { border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-add-benefit-btn:hover { background: #162220; border-color: #900C27; color: #4ade80; }
body.dark-only .gl-toggle { background: #2d3f3b; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
</style>
